/**
 * Great-circle distance between two lat/lon pairs, in metres.
 * Haversine formula — accurate to within 0.5% for distances up to ~500km.
 * Not a routable distance — use ORS for actual driving distance.
 */

export const haversineMetres = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const R = 6_371_000; // Earth radius in metres
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Validates latitude and longitude against standard geographic bounds.
 * Latitude: [-90, 90]
 * Longitude: [-180, 180]
 */

export interface LocationCoords {
  latitude: number | string;
  longitude: number | string;
}

export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateLocation = (
  data: Partial<LocationCoords>,
): ValidationResult => {
  const lat = Number(data.latitude);
  const lng = Number(data.longitude);

  // Check for NaN or non-numeric input
  if (isNaN(lat) || isNaN(lng)) {
    return { isValid: false, error: 'Coordinates must be valid numbers' };
  }

  // Latitude bounds
  if (lat < -90 || lat > 90) {
    return { isValid: false, error: 'Latitude must be between -90 and 90' };
  }

  // Longitude bounds
  if (lng < -180 || lng > 180) {
    return { isValid: false, error: 'Longitude must be between -180 and 180' };
  }

  return { isValid: true };
};
